import { useRef } from "react";
import useAutoplayVideo from "../../hooks/useAutoplayVideo";
import { useLanguage } from "../../context/LanguageContext";

// Replaces the old image-slider banner (CatalogPosterSlider, see git
// history) with a looping video — reuses the same coffee-macro video the
// homepage's closing CTA section used before that section switched to a
// sliding-image background (see CtaBanner.tsx), so the file isn't just
// sitting unused once freed up from there.
export default function CatalogVideoBanner({
  className = "",
  brandName,
  brandDescription,
}: {
  className?: string;
  /** Set together — the sidebar's brand filter narrowed to exactly one
   * brand that has a description on file (see CatalogPage's
   * singleSelectedBrand/brandDescription). Swaps the generic heading for
   * the brand's own name + blurb while that filter is active. */
  brandName?: string;
  brandDescription?: string;
}) {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  useAutoplayVideo(videoRef);
  // Most brand descriptions are one short sentence and read fine at the
  // default size (see brandDescriptions.ts) — but a couple (Royale) run
  // long enough that at that size/width they wrap taller than the heading
  // beside them. Past this rough length, drop to a smaller size and let
  // the paragraph spread much wider instead, trading line count for width
  // so the block stays closer to the heading's own height.
  const isLongDescription = (brandDescription?.length ?? 0) > 150;

  return (
    <div
      className={`relative flex h-[50vh] min-h-[380px] max-h-[520px] w-full items-end overflow-hidden bg-steel-100 sm:h-[52vh] sm:min-h-[420px] sm:max-h-[640px] ${className}`}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        poster="/assets/video/catalog-banner-poster.jpg"
        src="/assets/video/cta-banner.mp4"
        preload="auto"
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        disableRemotePlayback
      />
      {/* Keeps the navbar (transparent/overlaid on this page, see Navbar's
          hasDarkHero) legible against the bright video — same technique as
          the homepage Hero video, but over the whole banner rather than
          just the top strip. Darkened a touch more than Hero's own overlay
          (25%) since this banner is much shorter, so the heading below
          sits close to the busy video content on every frame rather than
          just the occasional one. */}
      <div className="pointer-events-none absolute inset-0 bg-black/35" />

      {/* Extra darkening specifically behind the text band at the bottom —
          the uniform overlay above keeps the navbar legible across the
          whole banner, but the video's own busy detail still fights with
          the heading/description sitting right on top of it. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 to-transparent" />

      {/* The banner previously carried no text of its own — just the video
          — leaving this whole section blank aside from the navbar overlaid
          on top of it. A real H1 here also covers the default (no category
          selected) catalog view, which had no visible heading at all
          before this — see the sr-only one further down that only renders
          once a category is chosen. */}
      <div className="container-industrial relative z-10 pb-6 sm:pb-8">
        {/* key swaps the whole block on every brand-name change (including
            to/from the generic heading), forcing a remount — animate-
            reveal-in then plays as a genuine entrance for the new content
            instead of the old text just being silently replaced in place. */}
        <div
          key={brandName ?? "default"}
          className="animate-reveal-in flex flex-wrap items-start gap-x-6 gap-y-2"
        >
          {/* Same size as Hero's own H1 (text-[clamp(4rem,3rem+5vw,7.5rem)],
              leading-[1.05]) per request — this banner is shorter than
              Hero, but the heading reads as noticeably undersized next to
              it otherwise. */}
          <h1 className="font-display uppercase text-[clamp(4rem,3rem+5vw,7.5rem)] font-normal tracking-[0.3px] leading-[1.05] text-white">
            {brandName ?? t("catalogBannerHeading")}
          </h1>
          {brandDescription && (
            // items-start (not items-baseline) so this starts level with
            // the top of the name instead of sinking to its baseline, and
            // no line-clamp — the full text shows, wrapping under itself
            // beside the heading rather than being cut off with "…".
            // BRAND_DESCRIPTIONS entries need to stay genuinely short for
            // this to read well next to a multi-line heading; that's a
            // content-authoring constraint, not something this component
            // enforces.
            //
            // The extra mt- here isn't arbitrary: BravoRG's own metrics
            // (checked directly against BravoRG.ttf — cap-height 700/1000em,
            // hhea ascent 880/1000em) put roughly 0.14em of empty leading
            // above the heading's visible letter-tops at leading-[1.05], so
            // items-start alone (box-top to box-top) still left the
            // paragraph's own glyphs sitting visibly higher than "ROYALE"'s.
            <p
              className={`mt-3 leading-relaxed text-white/80 sm:mt-4 ${
                isLongDescription
                  ? "max-w-2xl text-xs sm:max-w-3xl sm:text-sm"
                  : "max-w-sm text-sm sm:max-w-md sm:text-base"
              }`}
            >
              {brandDescription}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
